import { Can } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { Tabs } from '@/components/tabs/Tabs';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { AssignChargesForm } from '@/features/configuration-management/charge-management/AssignCharges';
import { encryptedAgentTypeMap } from '@/features/configuration-management/charge-management/charge.constant';
import { useCharges } from '@/features/configuration-management/charge-management/hooks/useChargesList';
import { ChargesTable } from '@/features/configuration-management/charge-management/table';
import { useDynamicLocale } from '@/hoslog-intl';
import { UsersCog } from '@/icons';
import { ChargeType } from '@/services/charges/charges.queries';
import { createFileRoute, SearchSchemaInput, useSearch } from '@tanstack/react-router';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum(['categoryType', 'createdBy', 'createdDate', 'status', 'description', 'categoryName']),
  desc: z.boolean(),
});

const chargesSearchSchema = z
  .object({
    sort: z
      .preprocess((val) => {
        if (typeof val === 'string') {
          try {
            return JSON.parse(val);
          } catch {
            return undefined;
          }
        }
        return val;
      }, z.array(sortItemSchema))
      .optional(),

    createdDate: z.string().optional(),

    q: z
      .any()
      .optional()
      .transform((val) => (val == null ? undefined : String(val))),

    status: z
      .string()
      .transform((val) => val.split(','))
      .pipe(z.array(z.enum(['ACTIVE', 'BLOCKED'])))
      .optional(),

    page: z.coerce.number().positive().catch(0),
    activeTab: z.enum(['club', 'affiliate', 'customer']).default('affiliate'),
    fetch: z.coerce.number().positive().max(100).catch(10),
  })
  .passthrough()
  .transform((data) => {
    return {
      ...data,
      agentType:
        data.activeTab === 'club'
          ? encryptedAgentTypeMap.club
          : data.activeTab === 'affiliate'
            ? encryptedAgentTypeMap.affiliate
            : encryptedAgentTypeMap.customer,
    };
  });

export const Route = createFileRoute('/(app)/configuration-management/charges/')({
  loader: ({ context }) => {
    const {
      dialogFormStore: { onOpen },
    } = context;

    const renderActions = (
      <Can rule={{ allOf: [PERMS.charge.assign] }}>
        <div>
          <Button
            variant="outline"
            className="border-foreground"
            onClick={() => {
              onOpen({
                modalTitle: 'configurationManagement.charges.assignChargeModal.title',
                title: 'Assign New Role',
                okText: <CrumbTitle id="common.page.button.submit" />,
                size: 'lg',
                component: <AssignChargesForm />,
              });
            }}
          >
            <UsersCog />
            <CrumbTitle id="configurationManagement.charges.page.buttonTitle" />
          </Button>
        </div>
      </Can>
    );

    return {
      actions: { renderActions },
    };
  },
  component: RouteComponent,

  validateSearch: (search: SearchSchemaInput) => {
    return chargesSearchSchema.parse(search);
  },
});

function RouteComponent() {
  const { activeTab } = useSearch({ from: '/(app)/configuration-management/charges/' });
  const { t } = useDynamicLocale();
  const { charges, isLoading, meta } = useCharges(activeTab as ChargeType, !!activeTab);
  const tabItems = [
    {
      label: t('configurationManagement.charges.table.affiliate'),
      value: 'affiliate',
      content: <ChargesTable data={charges} isLoading={isLoading} pagination={meta} />,
    },
    {
      label: t('configurationManagement.charges.table.club'),
      value: 'club',
      content: <ChargesTable data={charges} isLoading={isLoading} pagination={meta} />,
    },
    {
      label: t('configurationManagement.charges.table.customer'),
      value: 'customer',
      content: <ChargesTable data={charges} isLoading={isLoading} pagination={meta} />,
    },
  ];

  const [activeTabProp, setActiveTabProp] = useQueryState('activeTab', parseAsString.withDefault('affiliate'));
  const [, setAgentType] = useQueryState('agentType', parseAsString.withDefault('6'));
  const [, setPage] = useQueryState('page', parseAsInteger.withDefault(0));
  const [, setCreatedDate] = useQueryState('createdDate', parseAsString);
  const [, setSearchQuery] = useQueryState('q', parseAsString);

  const onTabChange = (tab: string): void => {
    void Promise.all([
      setActiveTabProp(tab),
      setAgentType(encryptedAgentTypeMap[tab] ?? ''),
      setPage(0),
      setCreatedDate(null),
      setSearchQuery(null),
    ]);
  };

  return (
    <Tabs
      className="rounded-lg shadow bg-muted"
      listClassName="bg-muted"
      triggerClassName="bg-muted rounded-t-[8px] px-6 py-3"
      defaultActive={activeTabProp}
      items={tabItems}
      onChange={onTabChange}
    />
  );
}
